import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Typography from '@mui/material/Typography'
import { useQueryClient } from '@tanstack/react-query'
import { FormikButtons } from 'components/Formik/FormikButtons'
import Loader from 'components/Loader/Loader'
import { Form, Formik } from 'formik'
import { useGetSurvey } from 'pages/Survey/hooks/useGetSurvey'
import { usePostSubmitAnswers } from 'pages/Survey/hooks/usePostSubmitAnswers'
import { Question } from 'pages/Survey/Question'
import { SurveyQuestionTypes, surveyQuestionTypes } from 'pages/Survey/types'
import { fontFamilies, getResponsiveSize } from 'theme/styles.utils'
import userQueryKeys from 'utils/api/User/userQueryKeys'
import { useIsMobile } from 'utils/hooks/useBreakpoints'
import { important } from 'utils/styles'
import { useGetObjectTranslation } from 'utils/useGetObjectTranslation'
import * as Yup from 'yup'

type Props = {
  surveyId?: number
  onSkip: () => void
  onSent: (id: number) => void
}

export const Survey = ({ surveyId, onSkip, onSent }: Props) => {
  const [isSent, setIsSent] = useState(false)
  const { data, isLoading } = useGetSurvey(surveyId)
  const isMobile = useIsMobile()
  const fontSize = 2
  const fontSizeMobile = 1.8
  const { t } = useTranslation()
  const { mutate, isLoading: isPending } = usePostSubmitAnswers()
  const queryClient = useQueryClient()

  const title = data?.content?.find((q) => q.type === surveyQuestionTypes.TITLE)
  const completionMessage = data?.content?.find((q) => q.type === surveyQuestionTypes.COMPLETION_MESSAGE)
  const questions = data?.content?.filter(
    (q) =>
      !([surveyQuestionTypes.COMPLETION_MESSAGE, surveyQuestionTypes.TITLE] as SurveyQuestionTypes[]).includes(q.type)
  )
  const translatedTitle = useGetObjectTranslation(title?.translation, 'title')
  const completionMessageText = useGetObjectTranslation(completionMessage?.translation, 'title')

  const initialValues: Record<string, string | string[]> = questions?.reduce(
    (acc, question) => {
      switch (question.type) {
        case surveyQuestionTypes.TEXT:
          acc[question.id] = ''
          break
        case surveyQuestionTypes.TEXTAREA:
          acc[question.id] = ''
          break
        case surveyQuestionTypes.SINGLE:
          acc[question.id] = ''
          break
        case surveyQuestionTypes.MULTIPLE:
          acc[question.id] = []
          break
        case surveyQuestionTypes.LIKERT:
          acc[question.id] = ''
          break
        case surveyQuestionTypes.DROPDOWN:
          acc[question.id] = ''
          break
        case surveyQuestionTypes.MEDIA:
          acc[question.id] = ''
          break
      }

      return acc
    },
    {} as Record<string, string | string[]>
  )

  const validationSchema = Yup.object().shape(
    questions?.reduce(
      (acc, question) => {
        if (!question.isRequired) return acc

        switch (question.type) {
          case surveyQuestionTypes.TEXT:
            acc[question.id] = Yup.string().required()
            break
          case surveyQuestionTypes.TEXTAREA:
            acc[question.id] = Yup.string().required()
            break
          case surveyQuestionTypes.SINGLE:
            acc[question.id] = Yup.string().required()
            break
          case surveyQuestionTypes.MULTIPLE:
            acc[question.id] = Yup.array()
              .of(Yup.string())
              .required()
              .min(1, t('app.validations.required').replace('$path', t('app.validations.this_field')))
            break
          case surveyQuestionTypes.LIKERT:
            acc[question.id] = Yup.string().required()
            break
          case surveyQuestionTypes.DROPDOWN:
            acc[question.id] = Yup.string().required()
            break
          case surveyQuestionTypes.MEDIA:
            acc[question.id] = Yup.string().required(t('survey.video.required'))
            break
        }

        return acc
      },
      {} as Record<string, Yup.AnySchema | undefined>
    )
  )

  const onSubmit = (values: Record<string, string | string[]>) => {
    mutate({
      answers: values,
      survey: surveyId,
      onSuccess: () => {
        setIsSent(true)
      },
    })
  }

  return (
    <Loader isLoading={isLoading}>
      <Collapse in={!isSent}>
        <Box display="grid">
          <Typography
            variantMapping={{ body1: 'h2' }}
            textAlign="center"
            fontWeight={900}
            lineHeight={1.75}
            fontSize={important(getResponsiveSize(isMobile ? fontSizeMobile : fontSize, 'rem'))}
            color="textPrimaryLight"
          >
            {translatedTitle}
          </Typography>
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <Form>
              <Box display="grid" gap={4} padding={2}>
                {questions?.map((question) => <Question key={question.id} question={question} />)}
                <FormikButtons onBack={onSkip} textButtonCancel={'app.skip'} isLoading={isPending} />
              </Box>
            </Form>
          </Formik>
        </Box>
      </Collapse>
      <Collapse in={isSent}>
        <Box display="grid" mt={8} gap={4}>
          <Typography variant="h1" fontWeight="bold" align="center" fontFamily={fontFamilies.default}>
            {t('survey.thank_you')}
          </Typography>
          <Typography variant="body2">{completionMessageText}</Typography>
        </Box>
        <Box display="flex" justifyContent="center" mt={8}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              onSent(surveyId)
              queryClient.invalidateQueries({ queryKey: userQueryKeys.all })
              setIsSent(false)
            }}
          >
            {t('app.next')}
          </Button>
        </Box>
      </Collapse>
    </Loader>
  )
}
